// ---------------------------------------------------------------------------
// Example projects loadable from the welcome screen, without going through
// showDirectoryPicker() (which requires the user to pick a folder
// themselves). The files live as static assets in public/ejemplos/ (served
// by Vite) and are read with `fetch`, wrapped in an adapter that implements
// ReadableFolder (see fsReadable.ts) — so openProject()/
// loadSubBasinLayer() never need to know the source isn't a real
// directory: it's the exact same code that opens a real project.
//
// "Águeda (without GIS)" isn't a copy of the data: it points at the SAME
// folder as "Águeda (with GIS)", with a manifest that simply doesn't include
// "GIS" — loadSubBasinLayer() doesn't find it, gisLayer stays null, and the
// workspace falls back to the canvas without GIS (same behavior as a real
// project with no layer linked). Zero duplicated data.
//
// v1.0 project (see CLAUDE.md, "Compatibility with v1.0 projects" — .inp
// series files, index-based column linking), unlike the previous Turia
// example (v2). Already fully solved (every sub-basin has a zone and an
// outlet point assigned in the .ehd itself), so completeTopology() below
// isn't needed for it — kept exported (and tested) in case a future
// example needs it again.
// ---------------------------------------------------------------------------
import type { TextKey } from "../i18n/es.ts";
import { TranslatableError, type Translate } from "../i18n/translate.ts";
import type { ReadableFile, ReadableFolder } from "./fsReadable.ts";
import {
  addOutletPoint,
  addZone,
  assignSurfaceOutletInBulk,
  assignZoneInBulk,
  NO_MODEL,
} from "./projectEntities.ts";
import type { EvalHidProject } from "./types.ts";

type ExampleNode = { [name: string]: "file" | ExampleNode };

class FetchFile implements ReadableFile {
  readonly kind = "file" as const;
  constructor(private url: string) {}

  async getFile() {
    const res = await fetch(this.url);
    if (!res.ok) {
      throw new TranslatableError("examples.fileLoadError", {
        url: this.url,
        status: res.status,
      });
    }
    const bytes = await res.arrayBuffer();
    return {
      size: bytes.byteLength,
      name: this.url.split("/").pop() ?? this.url,
      async arrayBuffer() {
        return bytes;
      },
      async text() {
        return new TextDecoder("windows-1252").decode(bytes);
      },
    };
  }
}

class FetchFolder implements ReadableFolder {
  readonly kind = "directory" as const;
  constructor(
    private baseUrl: string,
    private node: ExampleNode
  ) {}

  async *entries(): AsyncIterable<[string, ReadableFile | ReadableFolder]> {
    for (const name of Object.keys(this.node)) {
      const value = this.node[name];
      yield [
        name,
        value === "file"
          ? new FetchFile(`${this.baseUrl}/${name}`)
          : new FetchFolder(`${this.baseUrl}/${name}`, value),
      ];
    }
  }

  async getFileHandle(name: string): Promise<ReadableFile> {
    if (this.node[name] !== "file") {
      throw new TranslatableError("examples.fileNotFoundError", { name });
    }
    return new FetchFile(`${this.baseUrl}/${name}`);
  }

  async getDirectoryHandle(name: string): Promise<ReadableFolder> {
    const sub = this.node[name];
    if (sub == null || sub === "file") {
      throw new TranslatableError("examples.folderNotFoundError", { name });
    }
    return new FetchFolder(`${this.baseUrl}/${name}`, sub);
  }
}

const AGUEDA_FILES_WITH_GIS: ExampleNode = {
  "Agueda.ehd": "file",
  Dir1: {
    "Apo_Precip.inp": "file",
    "Apo_ETP.inp": "file",
    "Apo_Temp.inp": "file",
  },
  GIS: {
    "SubCabAgueda.shp": "file",
    "SubCabAgueda.dbf": "file",
    "SubCabAgueda.prj": "file",
    "SubCabAgueda.shx": "file",
  },
};

// Same project, without the shapefile folder.
const AGUEDA_FILES_WITHOUT_GIS: ExampleNode = {
  "Agueda.ehd": "file",
  Dir1: {
    "Apo_Precip.inp": "file",
    "Apo_ETP.inp": "file",
    "Apo_Temp.inp": "file",
  },
};

const BASE_URL_AGUEDA = `${import.meta.env.BASE_URL}ejemplos/agueda`;

/**
 * The real Turia .ehd (provided for testing, see CLAUDE.md) doesn't come
 * with its topology finished — that's normal, the original lets you save a
 * half-configured project: all 77 sub-basins already have a rainfall-runoff
 * model (HBV) but no zone wrapping it, and none has an outlet point
 * assigned. So the example is more than a map full of gray notices, it's
 * completed IN MEMORY (the real .ehd on disk is never touched) with the
 * bare minimum, and only from data that's ALREADY in the project itself:
 *
 *   1. Every sub-basin with a model but no zone is assigned to a zone that
 *      already has that same model (Turia's .ehd brings "ARQUILLO", with
 *      HBV, so all 77 end up there — the model isn't invented, only the
 *      zone wrapper our schema requires gets filled in). If there were no
 *      zone with that model, a new one is created with its default
 *      parameters.
 *   2. Every sub-basin without an outlet point is assigned to the first one
 *      the project already brings (Turia brings "ARQUILLO", "BENAGEBER",
 *      "LORIGUILLA" — real Turia reservoir names); if there were none, one
 *      is created.
 *
 * Sub-basins that ALREADY had no model at all in the original .ehd are left
 * as-is (with no zone) on purpose, so the example keeps honestly showing
 * that there's incomplete data.
 */
export function completeTopology(originalProject: EvalHidProject, t: Translate): EvalHidProject {
  let project = originalProject;

  const modelsWithoutZone = new Set(
    project.subBasins
      .filter((c) => c.zoneCode == null && c.modelTypeCode !== NO_MODEL)
      .map((c) => c.modelTypeCode)
  );
  for (const modelTypeCode of modelsWithoutZone) {
    let zone = project.zones.find((z) => z.modelTypeCode === modelTypeCode);
    if (!zone) {
      const kind = project.modelTypes.find(
        (t) => t.modelTypeCode === modelTypeCode && t.kind === 0
      );
      if (!kind) continue;
      project = addZone(
        project,
        t("examples.zoneFallbackName", { model: kind.modelTypeName }),
        modelTypeCode,
        0
      );
      zone = project.zones[project.zones.length - 1];
    }
    const codes = new Set(
      project.subBasins
        .filter((c) => c.zoneCode == null && c.modelTypeCode === modelTypeCode)
        .map((c) => c.subBasinCode)
    );
    project = assignZoneInBulk(project, codes, "zoneCode", zone.zoneCode);
  }

  const withoutOutlet = new Set(
    project.subBasins.filter((c) => c.outletCode == null).map((c) => c.subBasinCode)
  );
  if (withoutOutlet.size > 0) {
    let point = project.outletPoints[0];
    if (!point) {
      project = addOutletPoint(project, "Desagüe");
      point = project.outletPoints[project.outletPoints.length - 1];
    }
    project = assignSurfaceOutletInBulk(project, withoutOutlet, point.outletCode);
  }

  return project;
}

export interface ExampleProject {
  id: string;
  /** Text keys (not literals) — resolved with useT() when displayed, see Landing.tsx. */
  title: TextKey;
  description: TextKey;
  folder: () => ReadableFolder;
  /** In-memory adjustment after loading (see completeTopology) — doesn't touch the real .ehd. */
  adjustProject?: (project: EvalHidProject, t: Translate) => EvalHidProject;
}

export const EXAMPLES: ExampleProject[] = [
  {
    id: "agueda-gis",
    title: "examples.aguedaGis.title",
    description: "examples.aguedaGis.description",
    folder: () => new FetchFolder(BASE_URL_AGUEDA, AGUEDA_FILES_WITH_GIS),
  },
  {
    id: "agueda-sin-gis",
    title: "examples.aguedaSinGis.title",
    description: "examples.aguedaSinGis.description",
    folder: () => new FetchFolder(BASE_URL_AGUEDA, AGUEDA_FILES_WITHOUT_GIS),
  },
];
